export default class Mapbox {
	static loadDependencies() {
		const mapboxVersion = 'v3.28.0'; // previously v3.28.0

		const loadJS = function() {
			return new Promise( ( resolve, reject ) => {
				const script = document.createElement( 'script' );
				script.src = `https://api.mapbox.com/mapbox-gl-js/${mapboxVersion}/mapbox-gl.js`;

				script.onload = () => {
					resolve();

					return;
				};

				script.onerror = () => {
					reject( new Error( 'mapbox gl js failed to load' ) );

					return;
				};

				script.async = true;

				const first = document.head.getElementsByTagName( 'script' )[0];
				first.parentNode.insertBefore( script, first );
			} );
		};

		const loadCSS = function() {
			return new Promise( ( resolve, reject ) => {
				const link = document.createElement( 'link' );
				link.rel = 'stylesheet';
				link.href = `https://api.mapbox.com/mapbox-gl-js/${mapboxVersion}/mapbox-gl.css`;

				link.onload = () => {
					resolve();

					return;
				};

				link.onerror = () => {
					reject( new Error( 'mapbox gl css failed to load' ) );

					return;
				};

				const first = document.head.getElementsByTagName( 'link' )[0];
				first.parentNode.insertBefore( link, first );
			} );
		};

		return Promise.all( [
			loadJS(),
			loadCSS(),
		] );
	}

	static initializeMap( node, bounds ) {
		return new Promise( ( resolve ) => {
			const map = new window.mapboxgl.Map( {
				container: node,
				style: 'mapbox://styles/mrhenry/cmsh8sdo300is01pd0fd2dzkb?optimize=true',
				cooperativeGestures: true,
				bounds: bounds,
			} );

			map.dragRotate.disable();
			map.touchZoomRotate.disableRotation();

			const nav = new window.mapboxgl.NavigationControl( {
				showCompass: false,
				showZoom: true,
			} );

			map.addControl( nav, 'top-left' );
			map.addControl( new window.mapboxgl.FullscreenControl(), 'top-left' );

			// eslint-disable-next-line no-undef
			map.addControl( new mapboxgl.GeolocateControl( {
				positionOptions: {
					enableHighAccuracy: true,
				},
				trackUserLocation: true,
			} ) );

			map.on( 'load', () => {
				return resolve( map );
			} );
		} );
	}

	static renderMarkers( geoJSON, map ) {
		var hoveredClusterId = null;
		var hoveredPointId = null;

		map.addSource( 'markers', {
			type: 'geojson',
			data: geoJSON,
			generateId: true,
			cluster: true,
			clusterMaxZoom: 14, // Max zoom to cluster points on
			clusterRadius: 10, // Radius of each cluster when clustering points (defaults to 50)
		} );

		map.addLayer( {
			id: 'clusters',
			type: 'circle',
			source: 'markers',
			filter: [
				'has',
				'point_count',
			],
			paint: {
				'circle-color': [
					'case',
					[
						'boolean',
						[
							'feature-state',
							'hover',
						],
						false,
					],
					'#000000',
					'#838280',
				],
				'circle-radius': 10,
				'circle-emissive-strength': 1,
			},
		} );

		map.addLayer( {
			id: 'cluster-count',
			type: 'symbol',
			source: 'markers',
			filter: [
				'has',
				'point_count',
			],
			layout: {
				'text-field': [
					'get',
					'point_count_abbreviated',
				],
				'text-font': [
					'Plus Jakarta Sans Bold',
					'Open Sans Bold',
				],
				'text-size': 8,
			},
			paint: {
				'text-color': '#ffffff',
			},
		} );

		map.addLayer( {
			id: 'unclustered-point',
			type: 'circle',
			source: 'markers',
			filter: [
				'!',
				[
					'has',
					'point_count',
				],
			],
			paint: {
				'circle-color': [
					'case',
					[
						'boolean',
						[
							'feature-state',
							'hover',
						],
						false,
					],
					'#000000',
					'#838280',
				],
				'circle-radius': 10,
				'circle-emissive-strength': 1,
			},
		} );

		// When a click event occurs on a cluster,
		// getClusterExpansionZoom grabs the zoomlevel where the cluster expands
		// Then the viewport zooms in to show the expanded cluster
		// Displaying the underlying individual points and/or smaller clusters
		map.addInteraction( 'click-clusters', {
			type: 'click',
			target: {
				layerId: 'clusters',
			},
			handler: ( e ) => {
				const features = map.queryRenderedFeatures( e.point, {
					layers: [
						'clusters',
					],
				} );
				const clusterId = features[0].properties.cluster_id;
				map.getSource( 'markers' ).getClusterExpansionZoom(
					clusterId,
					( err, zoom ) => {
						if ( err ) return;

						map.easeTo( {
							center: features[0].geometry.coordinates,
							zoom: zoom,
						} );
					}
				);
			},
		} );

		// When a click event occurs on a feature in
		// the unclustered-point layer, open a popup at
		// the location of the feature, with
		// description HTML from its properties.
		map.addInteraction( 'click-unclustered-point', {
			type: 'click',
			target: {
				layerId: 'unclustered-point',
			},
			handler: ( e ) => {
				const coordinates = e.feature.geometry.coordinates.slice();

				// eslint-disable-next-line no-undef
				new mapboxgl.Popup( {
					offset: 20,
				} )
					.setLngLat( coordinates )
					.setHTML( e.feature.properties.popup_html )
					.addTo( map );
			},
		} );

		// Change the cursor to a pointer when the mouse is over a cluster of POIs.
		map.addInteraction( 'clusters-mouseenter', {
			type: 'mouseenter',
			target: {
				layerId: 'clusters',
			},
			handler: ( e ) => {
				if ( hoveredClusterId !== null ) {
					map.setFeatureState(
						{
							source: 'markers',
							id: hoveredClusterId,
						},
						{
							hover: false,
						}
					);
				}

				hoveredClusterId = e.feature.id;
				map.setFeatureState(
					{
						source: 'markers',
						id: hoveredClusterId,
					},
					{
						hover: true,
					}
				);

				map.getCanvas().style.cursor = 'pointer';
			},
		} );

		// Change the cursor back to a pointer when it stops hovering over a cluster of POIs.
		map.addInteraction( 'clusters-mouseleave', {
			type: 'mouseleave',
			target: {
				layerId: 'clusters',
			},
			handler: () => {
				if ( hoveredClusterId !== null ) {
					map.setFeatureState(
						{
							source: 'markers',
							id: hoveredClusterId,
						},
						{
							hover: false,
						}
					);
				}

				map.getCanvas().style.cursor = '';
			},
		} );

		// Change the cursor to a pointer when the mouse is over an individual POI.
		map.addInteraction( 'unclustered-mouseenter', {
			type: 'mouseenter',
			target: {
				layerId: 'unclustered-point',
			},
			handler: ( e ) => {
				if ( hoveredPointId !== null ) {
					map.setFeatureState(
						{
							source: 'markers',
							id: hoveredPointId,
						},
						{
							hover: false,
						}
					);
				}

				hoveredPointId = e.feature.id;
				map.setFeatureState(
					{
						source: 'markers',
						id: hoveredPointId,
					},
					{
						hover: true,
					}
				);

				map.getCanvas().style.cursor = 'pointer';
			},
		} );

		// Change the cursor back to a pointer when it stops hovering over an individual POI.
		map.addInteraction( 'unclustered-mouseleave', {
			type: 'mouseleave',
			target: {
				layerId: 'unclustered-point',
			},
			handler: () => {
				if ( hoveredPointId !== null ) {
					map.setFeatureState(
						{
							source: 'markers',
							id: hoveredPointId,
						},
						{
							hover: false,
						}
					);
				}

				map.getCanvas().style.cursor = '';
			},
		} );
	}
}
